import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { MapPinIcon, PhoneIcon, ClockIcon, NavigationIcon, CarIcon, BusIcon, AccessibilityIcon } from 'lucide-react';
const locations = [{
  id: 'landover',
  name: 'Landover Clinic',
  address: '8700 Central Ave #302A',
  city: 'Landover, MD 20785',
  phone: '(301) 613-9295',
  hours: [{
    day: 'Monday - Friday',
    time: '8:00 AM - 6:00 PM'
  }, {
    day: 'Saturday',
    time: '9:00 AM - 2:00 PM'
  }, {
    day: 'Sunday',
    time: 'Closed'
  }],
  directions: 'Located in the Central Avenue Professional Building. Ample free parking available in the front lot.',
  transit: 'Accessible via Metrobus routes F14 and V12. 10-minute walk from Morgan Blvd Metro Station (Silver/Blue lines).',
  mapUrl: 'https://maps.google.com/?q=8700+Central+Ave+Landover+MD'
}, {
  id: 'oxon',
  name: 'Oxon Hill Clinic',
  address: '6188 Oxon Hill Rd #601',
  city: 'Oxon Hill, MD 20745',
  phone: '(240) 553-7993',
  hours: [{
    day: 'Monday - Friday',
    time: '8:00 AM - 7:00 PM'
  }, {
    day: 'Saturday',
    time: '9:00 AM - 2:00 PM'
  }, {
    day: 'Sunday',
    time: 'Closed'
  }],
  directions: 'Situated near the National Harbor interchange. Enter through the main lobby; take elevator to the 6th floor.',
  transit: 'Accessible via Metrobus routes NH1, NH2, and D14. Bus stop located directly across the street.',
  mapUrl: 'https://maps.google.com/?q=6188+Oxon+Hill+Rd+Oxon+Hill+MD'
}];
export function FindUsPage() {
  return <>
      <PageHero eyebrow="Find Us" title="Our Locations" subtitle="Conveniently located in Landover and Oxon Hill to serve the greater community." bgImage="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1920&q=80" data-id="element-483" />

      <section className="py-16 md:py-24 bg-gray-50" data-id="element-484">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-485">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10" data-id="element-486">
            {locations.map((loc, idx) => <motion.div key={loc.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: idx * 0.2
          }} className="bg-white rounded-[10px] shadow-sm border border-[#E5E9F0] overflow-hidden flex flex-col" data-id="element-487">
                {/* Map Placeholder */}
                <div className="h-64 bg-gray-200 relative flex items-center justify-center border-b border-[#E5E9F0]" data-id="element-488">
                  <div className="absolute inset-0 opacity-30" style={{
                backgroundImage: 'radial-gradient(#0A71B1 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }} data-id="element-489" />
                  <div className="relative z-10 flex flex-col items-center text-[#0A71B1]" data-id="element-490">
                    <MapPinIcon className="h-12 w-12 mb-2 drop-shadow-md" fill="white" data-id="element-491" />
                    <span className="font-bold bg-white/90 px-4 py-1.5 rounded-full shadow-sm text-sm" data-id="element-492">
                      Interactive Map Placeholder
                    </span>
                  </div>
                </div>

                <div className="p-8 flex-grow flex flex-col" data-id="element-493">
                  <h2 className="text-2xl font-bold text-navy-500 mb-6" data-id="element-494">
                    {loc.name}
                  </h2>

                  <div className="space-y-4 mb-8" data-id="element-495">
                    <div className="flex items-start gap-3" data-id="element-496">
                      <MapPinIcon className="h-5 w-5 text-[#0A71B1] mt-1 flex-shrink-0" data-id="element-497" />
                      <div data-id="element-498">
                        <p className="text-gray-700 font-medium" data-id="element-499">
                          {loc.address}
                        </p>
                        <p className="text-gray-700 font-medium" data-id="element-500">{loc.city}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-501">
                      <PhoneIcon className="h-5 w-5 text-[#0A71B1] flex-shrink-0" data-id="element-502" />
                      <a href={`tel:${loc.phone.replace(/\D/g, '')}`} className="text-[#0A71B1] font-bold hover:underline text-lg" data-id="element-503">
                        {loc.phone}
                      </a>
                    </div>
                  </div>

                  <div className="mb-8" data-id="element-504">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-2" data-id="element-505">
                      <ClockIcon className="h-4 w-4" data-id="element-506" /> Hours of Operation
                    </h3>
                    <ul className="space-y-2" data-id="element-507">
                      {loc.hours.map(h => <li key={h.day} className="flex justify-between text-sm" data-id="element-508">
                          <span className="text-gray-600" data-id="element-509">{h.day}</span>
                          <span className="font-semibold text-navy-500" data-id="element-510">
                            {h.time}
                          </span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="space-y-4 mb-8 flex-grow" data-id="element-511">
                    <div className="flex items-start gap-3" data-id="element-512">
                      <CarIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-513" />
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-514">
                        {loc.directions}
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-515">
                      <BusIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-516" />
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-517">
                        {loc.transit}
                      </p>
                    </div>
                  </div>

                  <a href={loc.mapUrl} target="_blank" rel="noopener noreferrer" className="mt-auto inline-flex items-center justify-center w-full bg-[#E8891D] hover:bg-[#d07a15] text-white py-3.5 rounded-full font-bold transition-colors gap-2" data-id="element-518">
                    <NavigationIcon className="h-5 w-5" data-id="element-519" /> Get Directions
                  </a>
                </div>
              </motion.div>)}
          </div>

          {/* Accessibility Note */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-12 bg-blue-50 border border-blue-100 rounded-[10px] p-6 flex items-start gap-4" data-id="element-520">
            <AccessibilityIcon className="h-8 w-8 text-[#0A71B1] flex-shrink-0" data-id="element-521" />
            <div data-id="element-522">
              <h3 className="font-bold text-navy-500 mb-1" data-id="element-523">
                Accessibility Commitment
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-524">
                Both our Landover and Oxon Hill clinics are fully wheelchair
                accessible, featuring ramp entrances, elevator access, and
                ADA-compliant restrooms. If you require specific accommodations
                during your visit, please let our staff know when booking your
                appointment.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </>;
}